import React, { useCallback, useContext, useState } from 'react';
import { useFinantialOthersLoader } from '../parts/FinantialOthersLoader';
import {
  AcademicLevelType,
  CertificateType,
  PetitionType,
  StudyPlanTitulationDraft,
} from '../type';

interface IFinantialOthersStateContext {
  certificateTypesState: CertificateType[];
  setCertificateTypesState: React.Dispatch<
    React.SetStateAction<CertificateType[]>
  >;
  petitionTypesState: PetitionType[];
  setPetitionTypesState: React.Dispatch<React.SetStateAction<PetitionType[]>>;
  academicLevelsPriceState: AcademicLevelType[];
  setAcademicLevelsPriceState: React.Dispatch<
    React.SetStateAction<AcademicLevelType[]>
  >;
  // Borradores de "derecho a grado" por nivel académico, para guardarlos en
  // bloque con "GUARDAR CONFIGURACIÓN" (no de forma individual en el detalle).
  titulationDraftsState: Record<number, StudyPlanTitulationDraft>;
  setTitulationDraftForLevel: (
    academicLevelId: number,
    draft: StudyPlanTitulationDraft,
  ) => void;
}

const FinantialOthersStateContext = React.createContext<
  IFinantialOthersStateContext | undefined
>(undefined);

export const FinantialOtherStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data } = useFinantialOthersLoader();

  const [certificateTypesState, setCertificateTypesState] = useState<
    CertificateType[]
  >(data?.certificateTypes ?? []);
  const [petitionTypesState, setPetitionTypesState] = useState<PetitionType[]>(
    data?.petitionTypes ?? [],
  );

  const [academicLevelsPriceState, setAcademicLevelsPriceState] = useState<
    AcademicLevelType[]
  >(data?.academicLevels ?? []);

  const [titulationDraftsState, setTitulationDraftsState] = useState<
    Record<number, StudyPlanTitulationDraft>
  >({});

  const setTitulationDraftForLevel = useCallback(
    (academicLevelId: number, draft: StudyPlanTitulationDraft) => {
      setTitulationDraftsState((prev) => ({
        ...prev,
        [academicLevelId]: draft,
      }));
    },
    [],
  );

  return (
    <FinantialOthersStateContext.Provider
      value={{
        certificateTypesState,
        setCertificateTypesState,
        petitionTypesState,
        setPetitionTypesState,
        academicLevelsPriceState,
        setAcademicLevelsPriceState,
        titulationDraftsState,
        setTitulationDraftForLevel,
      }}
    >
      {children}
    </FinantialOthersStateContext.Provider>
  );
};

export const useFinantialOthersState = () => {
  const context = useContext(FinantialOthersStateContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
